import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Field,
  Input,
  Heading,
  VStack,
  Text,
} from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { toast } from '../utils/toast';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast({
        title: 'Error',
        description: 'Reset link is invalid. Request a new one from your administrator.',
        status: 'error',
      });
      return;
    }
    if (newPassword.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters.',
        status: 'error',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'New password and confirmation do not match.',
        status: 'error',
      });
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      toast({
        title: 'Success',
        description: 'Password has been set. You can log in now.',
        status: 'success',
      });
      navigate('/login');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Setting password failed.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
        px={4}
      >
        <Container maxW="md">
          <Box bg="white" p={8} borderRadius="2xl" boxShadow="lg">
            <Heading size="lg" mb={4}>Password reset</Heading>
            <Text color="gray.600" mb={6}>
              Reset link is invalid or has expired. Contact your administrator to send you a new link.
            </Text>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              borderRadius="lg"
            >
              Back to login
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
      px={4}
    >
      <Container maxW="md">
        <Box
          bg="white"
          p={10}
          borderRadius="2xl"
          boxShadow="2xl"
        >
          <VStack gap={6} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="xl" mb={2} bgGradient="to-r" gradientFrom="purple.600" gradientTo="blue.600" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                New password
              </Heading>
              <Text color="gray.600" fontSize="sm">
                Enter your new password (min. 8 characters).
              </Text>
            </Box>

            <form onSubmit={handleSubmit}>
              <VStack gap={5}>
                <Field.Root required>
                  <label htmlFor="reset-new-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">New password</Text>
                  </label>
                  <Input
                    id="reset-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    size="lg"
                    borderRadius="lg"
                    minLength={8}
                  />
                </Field.Root>
                <Field.Root required>
                  <label htmlFor="reset-confirm-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">Confirm new password</Text>
                  </label>
                  <Input
                    id="reset-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    size="lg"
                    borderRadius="lg"
                  />
                </Field.Root>
                <Button
                  type="submit"
                  width="100%"
                  size="lg"
                  loading={loading}
                  bgGradient="to-r"
                  gradientFrom="purple.600"
                  gradientTo="blue.600"
                  color="white"
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                >
                  Set password
                </Button>
              </VStack>
            </form>
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
              Back to login
            </Button>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;
